import type { ReactNode } from "react";
import { createContext, useCallback, useMemo, useRef, useState } from "react";

import type { Carrier, CarrierPrice } from "~/shared/schemas/carrier-schema.ts";
import type {
	CreditLimit,
	CustomerDetails,
	PrimaryBroker,
} from "~/shared/schemas/customer-schema.ts";
import type {
	HazmatClass,
	HazmatGroup,
} from "~/shared/schemas/hazmat-schema.ts";
import type { QuoteResponse } from "~/shared/schemas/quote-schema.ts";
import type { ServiceLevel } from "~/shared/schemas/service-level-schema.ts";
import type {
	HandlingUnit,
	IdAndCarrierPrices,
	IdName,
	OnChange,
	QuoteDetailsContextType,
	QuoteStateType,
	UpdateMarginsParams,
} from "~/shared/types/quote-details-types.ts";
import {
	getStartOfToday,
	isPastShipDate,
} from "~/shared/utils/date-helpers.ts";
import type { QuoteWorkflow } from "~/shared/utils/google-analytics-helper.ts";
import { idGenerator } from "~/shared/utils/id-generator.ts";
import {
	formatDateToLocalISOString,
	formatPhoneNumber,
} from "~/shared/utils/string-fns.ts";

import { initialCarrierSelectionValues } from "../components/carrier-selection/constants/carrier-selection-constants.ts";
import {
	defaultCarrier,
	initVal,
} from "../components/quote-details/constants/quote-detail-constants.ts";
import { imperialMetricConverter } from "../utils/imperial-metric-converter.ts";
import { mapPostQuoteCommoditiesToHandlingUnits } from "../utils/map-post-quote-commodity-to-handling-unit.ts";
import {
	deriveCountry,
	updatePostalCode,
} from "../utils/quote-location-utils.ts";
import {
	QuoteAddressProvider,
	useQuoteAddressContext,
} from "./quote-address-context.tsx";
// for use when not wanting to save new quote
// import { mockQuoteData } from "./mocks.ts";

const QuoteDetailsContext = createContext<QuoteDetailsContextType>({
	quoteState: initVal,
	setQuoteState: () => {},
	resetForm: () => {},
	onChange: () => {},
	addItem: () => {},
	removeItem: () => {},
	updateItem: () => {},
	carrierSelectionState: initialCarrierSelectionValues,
	handleCarrierFilterAction: () => {},
	handleCarrierGlobalMarginAction: () => {},
	handleCarrierAction: () => {},
	handleItemUpdate: () => {},
	setCustomerDetails: () => {},
	updateItemsAndSummary: async () => {},
	setQuoteTotals: () => {},
	mergeResponseIntoQuoteState: () => {},
	loadQuoteFromResponse: () => {},
	imperialMetricConversion: () => {},
	setCreditDetails: () => {},
	updateAllCarrierPriceMargin: () => {},
	applyInsuranceToQuote: () => {},
	addBlankCarrier: () => {},
	removeBlankCarrier: () => {},
	updateCarrierPriceMargin: () => {},
	applyFoundCarrier: () => {},
	updateAdHocCarrier: () => {},
	setCustomerAr: () => {},
	removeAllBlankItems: () => {},
	workflow: "create_quote",
});

type QuoteDetailsProviderProps = {
	children: ReactNode;
	initialQuoteState?: QuoteStateType;
};

type QuoteDetailsContextBase = Omit<QuoteDetailsContextType, "resetForm">;

type QuoteDetailsContextBridgeProps = {
	readonly children: ReactNode;
	readonly baseValue: QuoteDetailsContextBase;
	readonly quoteState: QuoteStateType;
	readonly baseResetForm: () => void;
};

const QuoteDetailsProvider = ({
	children,
	initialQuoteState,
}: QuoteDetailsProviderProps) => {
	//---------------- Quote details State ---------------------- //
	const [quoteState, setQuoteState] = useState<QuoteStateType>(
		initialQuoteState ?? initVal,
	);
	// const [quoteState, setQuoteState] = useState<QuoteStateType>(mockQuoteData);
	// const [carriers, setCarriers] = useState([]);

	//---------------- Workflow tracking ---------------------- //
	const workflowRef = useRef<QuoteWorkflow>("create_quote");

	//---------------- Carrier Selection State ---------------------- //
	const [carrierSelectionState, setCarrierSelectionState] = useState(
		initialCarrierSelectionValues,
	);

	//---------------- Quote details Actions ---------------------- //
	const resetForm = useCallback(() => {
		setQuoteState(initVal);
	}, []);

	const setCreditDetails = useCallback(
		(data: { customerCredits: CreditLimit }) => {
			setQuoteState((prevState) => ({
				...prevState,
				customerCredits: data.customerCredits,
			}));
		},
		[],
	);

	// Use the OnChange type from QuoteDetailsContextType for type compatibility

	const onChange = useCallback(
		(field: keyof QuoteStateType, value: OnChange | null) => {
			setQuoteState((prevState) => ({
				...prevState,
				[field]: value,
			}));
		},
		[],
	);

	const addItem = useCallback((item: HandlingUnit) => {
		setQuoteState((prevState) => {
			const itemsValue = prevState.items;
			const next = [...itemsValue, { ...item, id: idGenerator() }];
			return { ...prevState, items: next };
		});
	}, []);

	const removeItem = useCallback((id: number) => {
		setQuoteState((prevState) => {
			const items = prevState.items;
			const filtered = items.filter((item) => item.id !== id);
			const next = filtered.length > 0 ? filtered : [...initVal.items];
			return { ...prevState, items: next };
		});
	}, []);

	const removeAllBlankItems = useCallback(() => {
		setQuoteState((prevState) => {
			const items = prevState.items;
			const filtered = items.filter(
				(item) =>
					item.description ||
					item.quantity ||
					// eslint-disable-next-line unicorn/explicit-length-check -- length is a prop on the object
					item.length ||
					item.width ||
					item.height ||
					item.weight ||
					item.nmfc,
			);
			return { ...prevState, items: filtered };
		});
	}, []);

	const updateItem = useCallback(
		(
			item: HandlingUnit,
			field: keyof HandlingUnit,
			value: OnChange | IdName | null,
		) => {
			setQuoteState((prevState) => {
				const foundItemIdx = prevState.items.findIndex(
					(stateItem) => stateItem.id === item.id,
				);

				if (foundItemIdx === -1) {
					return prevState;
				}

				const copyItems = [...prevState.items];
				const currentItem = copyItems[foundItemIdx];
				if (currentItem) {
					copyItems[foundItemIdx] = {
						...currentItem,
						[field]: value,
					};
				}

				return { ...prevState, items: copyItems };
			});
		},
		[],
	);

	const setCustomerDetails = useCallback((customerData: CustomerDetails) => {
		setQuoteState((prevState) => ({
			...prevState,
			customer: {
				...prevState.customer,
				...customerData,
				ltlBroker:
					customerData.ltlBroker === null
						? null
						: (customerData.ltlBroker ?? { id: 0, name: "", email: "" }),
			},
			customerDetails: {
				...prevState.customerDetails,
				...customerData,
				ltlBroker:
					customerData.ltlBroker === null
						? null
						: (customerData.ltlBroker ?? { id: 0, name: "", email: "" }),
			},
		}));
	}, []);

	const setCustomerAr = useCallback((customerArData: PrimaryBroker) => {
		setQuoteState((prevState) => ({
			...prevState,
			customerAr: {
				...prevState.customerAr,
				...customerArData,
			},
		}));
	}, []);

	const updateItemsAndSummary = useCallback(
		async (
			items: HandlingUnit[],
			summary: {
				totalCubicFeet: number;
				totalDensity: number;
				totalLinearFeet: number;
			},
		) => {
			setQuoteState((prev) => ({
				...prev,
				items,
				totalCubicFootage: summary.totalCubicFeet,
				totalDensity: summary.totalDensity,
				totalLinearFeet: summary.totalLinearFeet,
			}));
			await new Promise((resolve) => setTimeout(resolve, 0));
		},
		[],
	);

	const mergeResponseIntoQuoteState = useCallback(
		(dataToMerge: IdAndCarrierPrices) => {
			setQuoteState((prevState) => {
				return {
					...prevState,
					...dataToMerge,
				} as QuoteStateType;
			});
		},
		[],
	);

	const loadQuoteFromResponse = useCallback(
		(
			quoteResponse: QuoteResponse,
			hazmatClasses?: HazmatClass[],
			hazmatGroups?: HazmatGroup[],
			isResuming = true,
		) => {
			// Convert commodities to handling units
			const mappedItems = mapPostQuoteCommoditiesToHandlingUnits(
				quoteResponse.quoteCommodities || [],
				hazmatClasses,
				hazmatGroups,
			);

			setQuoteState((prevState) => {
				// Only mark as resumed if loading into a fresh state (id === 0)
				// AND the caller indicates this is truly a resume (not a
				// navigation from the create-quote flow). The provider remounts
				// when the route changes from /quote-details to
				// /carrier-selection/:quoteId, so prevState.id === 0 alone is
				// not sufficient to distinguish resume from create.
				if (prevState.id === 0 && isResuming) {
					workflowRef.current = "resume_quote";
				}

				const origin = quoteResponse.origin;
				const destination = quoteResponse.destination;

				const firstHazmatItem = quoteResponse.quoteCommodities?.find(
					(c) => c.isHazmat,
				);

				return {
					...prevState,
					id: quoteResponse.id,
					shipmentDate: isPastShipDate(quoteResponse.shipmentDate, false)
						? formatDateToLocalISOString(getStartOfToday())
						: quoteResponse.shipmentDate,
					// Keep the existing customer structure but update basic info
					customer: {
						...prevState.customer,
						id: quoteResponse.customer?.id ?? prevState.customer.id,
						name: quoteResponse.customer?.name ?? prevState.customer.name,
					},
					customerId: quoteResponse.customerId,
					pickupCountry: deriveCountry(
						prevState.pickupCountry,
						origin?.country,
					),
					deliveryCountry: deriveCountry(
						prevState.deliveryCountry,
						destination?.country,
					),
					pickupPostalCode: updatePostalCode(
						prevState.pickupPostalCode,
						origin,
					),
					deliveryPostalCode: updatePostalCode(
						prevState.deliveryPostalCode,
						destination,
					),
					items: mappedItems.length > 0 ? mappedItems : prevState.items,
					emergencyContactName:
						firstHazmatItem?.emergencyContactName ??
						firstHazmatItem?.hazmatDetail?.emergencyContactName ??
						"",
					emergencyContactPhone: formatPhoneNumber(
						firstHazmatItem?.emergencyContactPhone ??
							firstHazmatItem?.hazmatDetail?.emergencyContactPhone ??
							"",
					),
					carrierPrices: (quoteResponse.carrierPrices as CarrierPrice[]).map(
						(cp) => ({
							...cp,
							isAdHoc: cp.carrierPriceType?.id === 3,
						}),
					),
					selectedCarrierPriceId: quoteResponse.selectedCarrierPriceId ?? null,
					// Re-rate prev carrier
					quotePreviousRate:
						(quoteResponse.quotePreviousRate as CarrierPrice | null) ?? null,
					hasRoutingGuideMatch:
						quoteResponse.hasRoutingGuideMatch ??
						prevState.hasRoutingGuideMatch,
					pieceCaseCount: quoteResponse.pieceCaseCount,
					createdDate: quoteResponse.createdDate,
					expirationDate: quoteResponse.expirationDate,
					eTag: quoteResponse.eTag,
					totalLinearFeet: quoteResponse.calculatedLinearFeet ?? 0,
					customTotalLinearFeet:
						quoteResponse.calculatedLinearFeet === quoteResponse.totalLinearFeet
							? 0
							: (quoteResponse.totalLinearFeet ?? 0),
					customerReference: quoteResponse.customerReferences ?? "",
					poNumbers: quoteResponse.poNumbers ?? "",
					poNumber: quoteResponse.poNumber ?? null,
					soNumber: quoteResponse.soNumbers ?? "",
					specialInstructions: quoteResponse.specialInstructions ?? "",
					insuranceValue: quoteResponse.insuranceValue ?? null,
					insuranceApplyType: quoteResponse.insuranceApplyTypeId,
					insuranceApplyTypeId: quoteResponse.insuranceApplyTypeId,
					insuranceProductType: null,
					insuranceProductTypeId: quoteResponse.insuranceProductTypeId,
					// Map accessorials to pickup and delivery services based on stopType
					pickupServices: (quoteResponse.accessorials || []).filter(
						(a) => a.stopType?.name === "Pick",
					),
					deliveryServices: (quoteResponse.accessorials || []).filter(
						(a) => a.stopType?.name === "Drop",
					),
					addtDetails: (quoteResponse.accessorials || []).filter(
						(a) =>
							a.code === "BOND" ||
							a.code === "BLIND_D" ||
							a.code === "BLIND_S" ||
							a.code === "PFZ",
					),
					responseLinearFeet: quoteResponse.totalLinearFeet,
					publicGuid: quoteResponse.publicGuid,
					puNumber: quoteResponse.pickupDetails?.puNumber ?? "",
					deliveryPO: quoteResponse.deliveryDetails?.deliveryPO ?? "",
					billingCustomerId: quoteResponse.billingCustomerId ?? null,
				};
			});
		},
		[],
	);

	const setQuoteTotals = useCallback(() => {
		setQuoteState((prevState: QuoteStateType) => {
			const totalWeight = prevState.items.reduce(
				(acc, item) => acc + (item.weight ?? 0),
				0,
			);
			const totalQuantity = prevState.items.reduce(
				(acc, item) => acc + (item.quantity ?? 0),
				0,
			);
			let classString = "";
			for (const item of prevState.items) {
				classString += ` ${item.class?.name ?? ""}`;
			}
			return {
				...prevState,
				totalWeight,
				totalQuantity,
				class: classString.trim(),
			};
		});
	}, []);

	const imperialMetricConversion = useCallback(
		(type: string, handlingUnitId: number) => {
			setQuoteState((prevState) => {
				const handlingUnitIndex = prevState.items.findIndex(
					(unit) => unit.id === handlingUnitId,
				);
				if (handlingUnitIndex === -1) return prevState;

				const handlingUnit = prevState.items[handlingUnitIndex];
				if (!handlingUnit) return prevState;

				const convertedHandlingUnit = imperialMetricConverter(type, {
					weight: handlingUnit.weight,
					length: handlingUnit.length,
					width: handlingUnit.width,
					height: handlingUnit.height,
				});

				const updatedHandlingUnit: HandlingUnit = {
					...handlingUnit,
					weight: convertedHandlingUnit.weight ?? 0,
					length: convertedHandlingUnit.length ?? 0,
					width: convertedHandlingUnit.width ?? 0,
					height: convertedHandlingUnit.height ?? 0,
				};

				return {
					...prevState,
					items: [
						...prevState.items.slice(0, handlingUnitIndex),
						updatedHandlingUnit,
						...prevState.items.slice(handlingUnitIndex + 1),
					],
				};
			});
		},
		[],
	);

	//---------------- Carrier Selection Actions ---------------------- //

	type CarrierSelectionValueTypes = string | boolean | number;

	const handleCarrierFilterAction = useCallback(
		(name: string, value: CarrierSelectionValueTypes) => {
			setCarrierSelectionState((prev) => ({
				...prev,
				carrierFilter: {
					...prev.carrierFilter,
					[name]: value,
				},
			}));
		},
		[],
	);

	const handleCarrierGlobalMarginAction = useCallback(
		(name: string, value: CarrierSelectionValueTypes) => {
			setCarrierSelectionState({
				...carrierSelectionState,
				globalMargin: {
					...carrierSelectionState.globalMargin,
					[name]: value,
				},
			});
		},
		[carrierSelectionState],
	);

	const handleCarrierAction = useCallback(
		(name: string, value: CarrierSelectionValueTypes) => {
			setCarrierSelectionState({
				...carrierSelectionState,
				[name]: value,
			});
		},
		[carrierSelectionState],
	);

	// helper - deal with both types of margin updates
	const updateMargins = useCallback((params: UpdateMarginsParams) => {
		setQuoteState((prevState) => {
			const updatedCarriers = prevState.carrierPrices.map(
				(carrier: CarrierPrice) => {
					// Preserve any insurance markup that has been applied so changing
					// the margin doesn't strip the insurance cost from the customer rate.
					const insuranceMarkup = carrier.insuranceMarkup ?? 0;

					// If updating a single carrier, skip others
					if (params.carrierId != null) {
						if (carrier.id !== params.carrierId) return carrier;

						const originalBrokerMargin =
							carrier.originalBrokerMargin ?? carrier.brokerMargin;
						const brokerMargin = params.marginValue;
						return {
							...carrier,
							originalBrokerMargin,
							brokerMargin,
							customerRate:
								brokerMargin + carrier.carrierRate + insuranceMarkup,
						};
					}

					// Update all carriers path
					const originalBrokerMargin =
						carrier.originalBrokerMargin ?? carrier.brokerMargin;
					const brokerMargin =
						params.marginType === "percentage"
							? carrier.carrierRate * params.marginValue
							: params.marginValue;
					return {
						...carrier,
						originalBrokerMargin,
						brokerMargin,
						customerRate: brokerMargin + carrier.carrierRate + insuranceMarkup,
					};
				},
			);
			return {
				...prevState,
				carrierPrices: updatedCarriers,
				selectedCarrierPriceId: null,
			};
		});
	}, []);

	const updateCarrierPriceMargin = useCallback(
		(params: UpdateMarginsParams) => {
			updateMargins(params);
		},
		[updateMargins],
	);

	const updateAllCarrierPriceMargin = useCallback(
		(marginType: "percentage" | "flat", marginValue: number) => {
			updateMargins({ marginType, marginValue });
		},
		[updateMargins],
	);

	const applyInsuranceToQuote = useCallback(
		(insuranceData: {
			shipmentValue: number | null;
			applyTo: number | null;
			product: number | null;
			eTag: string;
		}) => {
			const { applyTo, product, shipmentValue, eTag } = insuranceData;
			setQuoteState((prevState) => ({
				...prevState,
				insuranceApplyTypeId: applyTo,
				insuranceProductTypeId: product,
				insuranceValue: shipmentValue,
				eTag,
			}));
		},
		[],
	);

	const addBlankCarrier = useCallback(() => {
		setQuoteState((prevState) => ({
			...prevState,
			carrierPrices: [
				// use neg numbers to keep new carriers unique
				{
					...defaultCarrier,
					id: idGenerator() * -1,
					carrierId: -1,
					isAdHoc: true,
					carrierPriceTypeId: 3, // manual
				},
				...prevState.carrierPrices,
			],
		}));
	}, []);

	const removeBlankCarrier = useCallback((id: number) => {
		setQuoteState((prevState) => {
			const updatedCarriers = prevState.carrierPrices.filter(
				(carrier) => carrier.id !== id,
			);
			return {
				...prevState,
				carrierPrices: updatedCarriers,
			};
		});
	}, []);

	const applyFoundCarrier = useCallback((carrier: Carrier) => {
		setQuoteState((prevState) => {
			const updatedCarriers = prevState.carrierPrices.map((exCarrier) => {
				return exCarrier.carrierId === -1
					? {
							...exCarrier,
							// Keep the original id (negative number) to prevent duplicate keys
							carrierId: carrier.id,
							quoteId: prevState.id,
							insuranceMarkup: null,
							name: carrier.name,
							scac: carrier.scac,
							isCarrierOfTheYear: carrier.isCarrierOfTheYear,
							isEconomy: carrier.isEconomy,
							isPreferred: carrier.isPreferred,
							carrier: {
								...exCarrier.carrier,
								id: carrier.id,
								name: carrier.name,
								scac: carrier.scac,
								isCarrierOfTheYear: carrier.isCarrierOfTheYear,
								isEconomy: carrier.isEconomy,
								isPreferred: carrier.isPreferred,
								displayName: carrier.displayName,
							},
						}
					: exCarrier;
			});
			return {
				...prevState,
				carrierPrices: updatedCarriers,
			};
		});
	}, []);

	const updateAdHocCarrier = useCallback(
		(
			carrierId: number,
			field: string,
			value: string | number | ServiceLevel | null,
		) => {
			setQuoteState((prevState) => {
				const updatedCarriers = prevState.carrierPrices.map((carrier) => {
					if (carrier.id === carrierId) {
						const newCarrierState = {
							...carrier,
							[field]: value,
						};

						if (
							field === "carrierRate" ||
							field === "brokerMargin" ||
							field === "insuranceMarkup"
						) {
							const newCarrierRate =
								field === "carrierRate"
									? (value as number)
									: newCarrierState.carrierRate;
							const newBrokerMargin =
								field === "brokerMargin"
									? (value as number)
									: newCarrierState.brokerMargin;
							const insuranceMarkup = newCarrierState.insuranceMarkup ?? 0;
							newCarrierState.customerRate =
								newCarrierRate + newBrokerMargin + insuranceMarkup;
						}
						return newCarrierState;
					}
					return carrier;
				});
				return {
					...prevState,
					carrierPrices: updatedCarriers,
				};
			});
		},
		[],
	);

	//---------------- Shipment Summary Actions ---------------------- //

	const handleItemUpdate = useCallback(
		(item: HandlingUnit, field: string, value: OnChange | IdName | null) => {
			// this isn't really set in stone since we are using mock data everywhere
			// should use savedQuoteState once API is up
			const foundItemIdx = quoteState.items.findIndex((i) => i.id === item.id);
			const copyItems = [...quoteState.items];
			if (foundItemIdx !== -1) {
				const updatedItem: HandlingUnit = { ...item, [field]: value };
				copyItems[foundItemIdx] = updatedItem;
				setQuoteState((prevState) => ({
					...prevState,
					items: copyItems,
				}));
			}
		},
		[quoteState],
	);

	const baseContextValue = useMemo<QuoteDetailsContextBase>(
		() => ({
			quoteState,
			setQuoteState,
			onChange,
			addItem,
			removeItem,
			updateItem,
			carrierSelectionState,
			handleCarrierFilterAction,
			handleCarrierGlobalMarginAction,
			handleCarrierAction,
			handleItemUpdate,
			setCustomerDetails,
			updateItemsAndSummary,
			setQuoteTotals,
			mergeResponseIntoQuoteState,
			loadQuoteFromResponse,
			imperialMetricConversion,
			setCreditDetails,
			updateAllCarrierPriceMargin,
			applyInsuranceToQuote,
			addBlankCarrier,
			removeBlankCarrier,
			updateCarrierPriceMargin,
			applyFoundCarrier,
			updateAdHocCarrier,
			setCustomerAr,
			removeAllBlankItems,
			workflow: workflowRef.current,
		}),
		[
			quoteState,
			setQuoteState,
			onChange,
			addItem,
			removeItem,
			updateItem,
			carrierSelectionState,
			handleCarrierFilterAction,
			handleCarrierGlobalMarginAction,
			handleCarrierAction,
			handleItemUpdate,
			setCustomerDetails,
			updateItemsAndSummary,
			setQuoteTotals,
			mergeResponseIntoQuoteState,
			loadQuoteFromResponse,
			imperialMetricConversion,
			setCreditDetails,
			updateAllCarrierPriceMargin,
			applyInsuranceToQuote,
			addBlankCarrier,
			removeBlankCarrier,
			updateCarrierPriceMargin,
			applyFoundCarrier,
			updateAdHocCarrier,
			setCustomerAr,
			removeAllBlankItems,
		],
	);

	return (
		<QuoteAddressProvider>
			<QuoteDetailsContextBridge
				baseResetForm={resetForm}
				baseValue={baseContextValue}
				quoteState={quoteState}
			>
				{children}
			</QuoteDetailsContextBridge>
		</QuoteAddressProvider>
	);
};

// cross context to access multiple contexts
const QuoteDetailsContextBridge = ({
	children,
	baseValue,
	baseResetForm,
}: QuoteDetailsContextBridgeProps) => {
	const { resetSelectedAddressBookItems } = useQuoteAddressContext();

	// Override resetForm to also reset addresses
	const resetForm = useCallback(() => {
		baseResetForm();
		resetSelectedAddressBookItems();
	}, [baseResetForm, resetSelectedAddressBookItems]);

	const contextValue = useMemo<QuoteDetailsContextType>(
		() => ({
			...baseValue,
			resetForm,
		}),
		[baseValue, resetForm],
	);

	return (
		<QuoteDetailsContext.Provider value={contextValue}>
			{children}
		</QuoteDetailsContext.Provider>
	);
};

export { QuoteDetailsContext, QuoteDetailsProvider };
